import React, { useState, useEffect } from 'react';
import { Camera, TrendingUp, MessageCircle, Calendar, Dumbbell, ChevronRight, Star, Users, Utensils, ArrowRight, Check, Sparkles, Play, Leaf, Menu, X, ChevronDown, Activity, Award, Watch, Droplets, FileText, HelpCircle, Plus, Minus, Info, HeartPulse, BarChart3, Video, Share2, Settings } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import LanguageSelector from './LanguageSelector';
import VisitorTimeoutModal from './VisitorTimeoutModal';

const LandingPage = ({ onGetStarted, onLogin, onPrivacy, onTerms, onAbout, onLegalPage }) => {
  const { t } = useTranslation();
  const [isVisible, setIsVisible] = useState({});
  const [activeFeature, setActiveFeature] = useState(0);
  const [menuOpen, setMenuOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [expandedFaq, setExpandedFaq] = useState(null);

  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  // FAQ Data
  const faqItems = [
    {
      question: "How does the AI food recognition work?",
      answer: "Simply take a photo of your meal, and our advanced AI analyzes it in seconds. It identifies individual foods, estimates portion sizes, and calculates calories, protein, carbs, and fats. The AI has been trained on millions of food images for accuracy rates over 95%."
    },
    {
      question: "Is NutriFitness AI free to use?",
      answer: "Yes! NutriFitness AI offers a free plan with 5 AI food scans per day, basic meal logging, and exercise tracking. For unlimited scans, personalized meal plans, AI coaching, and advanced analytics, you can upgrade to our Premium plan."
    },
    {
      question: "How accurate is the calorie counting?",
      answer: "Our AI provides calorie estimates within 5-10% accuracy for most common foods. For packaged foods, you can also scan barcodes for exact nutritional information. The more you use the app, the better it learns your portion sizes."
    },
    {
      question: "Can I track my exercises and workouts?",
      answer: "Absolutely! NutriFitness AI includes a comprehensive exercise tracker with 200+ exercises, custom workout creation, AI exercise coaching, and integration with popular fitness wearables like Fitbit. Track cardio, strength training, yoga, and more."
    },
    {
      question: "Does the app work offline?",
      answer: "Basic meal logging and exercise tracking work offline. However, AI food recognition and AI coaching features require an internet connection. Your data syncs automatically when you're back online."
    },
    {
      question: "How do I cancel my Premium subscription?",
      answer: "You can cancel your Premium subscription anytime from your account settings. There are no cancellation fees, and you'll continue to have Premium access until the end of your billing period. We also offer a 7-day free trial for new users."
    },
    {
      question: "Is my data private and secure?",
      answer: "Your privacy is our priority. All data is encrypted with 256-bit SSL, and we never sell your personal information. You can export or delete your data anytime. We comply with GDPR and other privacy regulations."
    },
    {
      question: "Can I use NutriFitness AI with dietary restrictions?",
      answer: "Yes! You can set dietary preferences like vegetarian, vegan, keto, gluten-free, halal, or any food allergies. The AI will flag foods that don't match your preferences and suggest alternatives in meal plans."
    },
    {
      question: "How do I get personalized meal recommendations?",
      answer: "Set your goals (weight loss, muscle gain, maintenance), dietary preferences, and daily calorie target in your profile. Our AI Nutritionist will then provide personalized meal suggestions, recipes, and weekly meal plans tailored to your needs."
    },
    {
      question: "Can I connect my fitness wearable or smartwatch?",
      answer: "NutriFitness AI integrates with Fitbit, Apple Health, Google Fit, and other popular fitness platforms. This allows automatic syncing of steps, heart rate, sleep data, and workouts for a complete health picture."
    },
    {
      question: "Can I track my water intake?",
      answer: "Yes! NutriFitness AI includes a dedicated water tracker. Set your daily hydration goal, log glasses or bottles of water with one tap, and receive reminders throughout the day to stay hydrated. You can also track other beverages like tea, coffee, and smoothies."
    },
    {
      question: "Does NutriFitness AI support multiple languages?",
      answer: "Yes! NutriFitness AI is available in multiple languages including English, Spanish, Portuguese, French, German, and more. You can change your language preference anytime in the settings. Our AI also recognizes foods from different cuisines worldwide."
    },
    {
      question: "Can I share my progress with friends or a trainer?",
      answer: "Absolutely! You can share your achievements, badges, and progress reports with friends, family, or your personal trainer. Export detailed PDF reports of your nutrition and exercise data, or connect with our community features to stay motivated together."
    },
    {
      question: "How do the achievement badges work?",
      answer: "Earn badges by completing milestones like logging meals consistently, hitting calorie goals, completing workouts, or maintaining streaks. Badges motivate you to stay on track and celebrate your progress. Premium members unlock exclusive achievement challenges."
    },
    {
      question: "Can I create custom recipes and meals?",
      answer: "Yes! Save your favorite meals and recipes for quick logging. Create custom recipes by adding ingredients, and NutriFitness AI will automatically calculate the nutritional information per serving. You can also browse our database of thousands of healthy recipes."
    },
    {
      question: "What is the AI Exercise Coach and how does it work?",
      answer: "Our AI Exercise Coach is your personal virtual trainer! Ask any fitness question and get instant, expert advice. It creates personalized workout plans based on your fitness level, goals, and available equipment. The AI coach provides exercise demonstrations, form tips, and adjusts your program as you progress."
    },
    {
      question: "What types of online exercises are available?",
      answer: "NutriFitness AI offers a complete library of 200+ exercises including strength training, cardio, HIIT, yoga, Pilates, stretching, and bodyweight workouts. Each exercise includes detailed instructions, video demonstrations, muscle groups targeted, and calorie burn estimates. Perfect for home or gym workouts!"
    },
    {
      question: "Can I get a personalized workout plan?",
      answer: "Yes! Our AI Workout Planner creates customized exercise programs based on your goals (weight loss, muscle building, endurance), fitness level, available time, and equipment. Get daily workout schedules, rest day recommendations, and progressive overload adjustments to keep challenging yourself."
    },
    {
      question: "How do I find healthy recipes that match my diet?",
      answer: "Our Recipe Hub has thousands of healthy recipes filtered by your dietary preferences, calorie goals, and ingredients you have. Find breakfast, lunch, dinner, and snack ideas with complete nutritional info. Save favorites, create shopping lists, and discover new cuisines from around the world."
    },
    {
      question: "Can the AI suggest recipes based on my calorie goals?",
      answer: "Absolutely! Tell our AI Nutritionist your daily calorie target and it will recommend recipes that fit perfectly. Whether you need a 400-calorie lunch or a protein-packed post-workout meal, the AI finds delicious options that match your macros and taste preferences."
    },
    {
      question: "Are there video tutorials for exercises?",
      answer: "Yes! Each exercise in our library includes step-by-step instructions and tips for proper form. The AI Coach can also explain any movement in detail, suggest modifications for beginners or injuries, and recommend alternative exercises if you don't have certain equipment."
    },
    {
      question: "Can I schedule live coaching sessions?",
      answer: "NutriFitness AI offers a booking system for live coaching sessions with certified fitness and nutrition professionals. Browse available coaches, view their specialties and ratings, and book one-on-one video consultations for personalized guidance on your health journey."
    }
  ];

  const toggleSubmenu = (menuId) => {
    setExpandedMenus(prev => ({
      ...prev,
      [menuId]: !prev[menuId]
    }));
  };

  // Menu structure for landing page - matches logged-in navigation
  const menuItems = [
    {
      id: 'exercise',
      label: t('nav.exercises').toUpperCase(),
      icon: Activity,
      isExpandable: true,
      subItems: [
        { label: t('exercises.title'), description: 'Browse exercises' },
        { label: t('nav.workoutWizard'), description: 'AI workout builder' },
        { label: t('nav.sessionLogger'), description: 'Log your sessions' },
        { label: t('nav.tracker'), description: 'Track workouts' },
        { label: t('nav.aiCoach'), description: 'Personal AI trainer' },
        { label: t('nav.planner'), description: 'Plan your routines' },
      ]
    },
    {
      id: 'meal',
      label: t('dashboard.meals').toUpperCase(),
      icon: Utensils,
      isExpandable: true,
      subItems: [
        { label: t('scanner.logMeal'), description: 'Scan & log food' },
        { label: t('session.history'), description: 'View past meals' },
        { label: t('nav.analytics'), description: 'Track progress' },
        { label: t('micro.title'), description: 'Vitamin & mineral tracking' },
        { label: t('adaptive.title'), description: 'Smart calorie goals' },
        { label: t('nav.whatToEat'), description: 'Get suggestions' },
        { label: t('nav.mealPlanner'), description: 'Plan your meals' },
      ]
    },
    {
      id: 'badges',
      label: t('dashboard.achievements').toUpperCase(),
      icon: Award,
      isExpandable: false
    },
    {
      id: 'ai-nutri',
      label: t('nav.aiNutri').toUpperCase(),
      icon: MessageCircle,
      isExpandable: false
    },
    {
      id: 'diet-recipes',
      label: t('nav.recipes').toUpperCase(),
      icon: Leaf,
      isExpandable: true,
      subItems: [
        { label: t('nav.organic'), description: 'Organic recipes' },
        { label: t('nav.vegan'), description: 'Plant-based meals' },
        { label: t('nav.glutenFree'), description: 'Gluten-free options' },
        { label: t('nav.heartHealthy'), description: 'Heart-smart choices' },
      ]
    },
    {
      id: 'wearables',
      label: t('nav.wearables').toUpperCase(),
      icon: Watch,
      isExpandable: false
    },
    {
      id: 'water',
      label: t('water.tracker').toUpperCase(),
      icon: Droplets,
      isExpandable: false
    },
    {
      id: 'referral',
      label: t('nav.referral').toUpperCase(),
      icon: Users,
      isExpandable: false
    },
    {
      id: 'marketing',
      label: t('nav.marketing').toUpperCase(),
      icon: BarChart3,
      isExpandable: false,
      adminOnly: true
    },
    {
      id: 'video-studio',
      label: t('nav.videoStudio').toUpperCase(),
      icon: Video,
      isExpandable: false
    },
    {
      id: 'social',
      label: t('nav.share').toUpperCase(),
      icon: Share2,
      isExpandable: false
    },
    {
      id: 'blog',
      label: t('nav.blog').toUpperCase(),
      icon: FileText,
      isExpandable: false
    },
    {
      id: 'how-it-works',
      label: t('nav.howItWorks').toUpperCase(),
      icon: HelpCircle,
      isExpandable: false
    },
    {
      id: 'account',
      label: t('nav.account').toUpperCase(),
      icon: Settings,
      isExpandable: false
    },
  ];

  useEffect(() => {
    // Animate elements on scroll
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible((prev) => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('[data-animate]').forEach((el) => {
      observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  // Auto-rotate features
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveFeature((prev) => (prev + 1) % 5);
    }, 4000);
    return () => clearInterval(interval);
  }, []);

  const features = [
    {
      icon: Camera,
      title: t('feature_ai_food'),
      description: t('feature_ai_food_desc'),
      color: 'from-[#FF6600] to-[#FF8533]',
      bgColor: 'bg-orange-50',
      stat: t('accuracy')
    },
    {
      icon: TrendingUp,
      title: t('feature_progress'),
      description: t('feature_progress_desc'),
      color: 'from-emerald-500 to-teal-500',
      bgColor: 'bg-emerald-50',
      stat: t('visual_progress')
    },
    {
      icon: MessageCircle,
      title: t('feature_nutritionist'),
      description: t('feature_nutritionist_desc'),
      color: 'from-blue-500 to-indigo-500',
      bgColor: 'bg-blue-50',
      stat: t('support_24_7')
    },
    {
      icon: Calendar,
      title: t('feature_meal_plan'),
      description: t('feature_meal_plan_desc'),
      color: 'from-purple-500 to-pink-500',
      bgColor: 'bg-purple-50',
      stat: t('personalized_plans')
    },
    {
      icon: Dumbbell,
      title: t('feature_exercise'),
      description: t('feature_exercise_desc'),
      color: 'from-red-500 to-orange-500',
      bgColor: 'bg-red-50',
      stat: t('exercises_count')
    },
    {
      icon: Leaf,
      title: 'Dietary Options',
      description: 'Personalized recipes for Organic, Vegan, and Gluten-Free diets tailored to your health goals.',
      color: 'from-green-500 to-emerald-500',
      bgColor: 'bg-green-50',
      stat: '3+ Diet Plans'
    }
  ];

  const stats = [
    { value: '200M+', label: t('meals_analyzed') },
    { value: '50K+', label: t('active_users') },
    { value: '4.8', label: t('app_rating'), icon: Star },
    { value: '15lbs', label: t('avg_weight_loss') }
  ];

  const steps = [
    { step: '01', title: t('step_1'), description: t('step_1_desc') },
    { step: '02', title: t('step_2'), description: t('step_2_desc') },
    { step: '03', title: t('step_3'), description: t('step_3_desc') }
  ];

  return (
    <div className="min-h-screen bg-white dark:bg-gray-800 overflow-x-hidden">
      {/* Navigation - Black Header with Hamburger Menu */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-2">
          {/* Mobile Layout - Logo left, Menu CENTER, Actions right */}
          <div className="lg:hidden flex items-center justify-between relative h-[80px]">
            {/* Left - Logo */}
            <img src="/logo-v4.png" alt="Logo" className="h-16 w-16 object-contain" />
            
            {/* CENTER - Hamburger Menu */}
            <div className="absolute left-1/2 -translate-x-1/2">
              <button
                onClick={() => setMenuOpen(true)}
                className="text-white p-3 hover:bg-gray-800 rounded-lg transition-colors flex flex-col justify-center items-center gap-1.5"
                aria-label="Open menu"
                data-testid="hamburger-menu-btn"
              >
                <span className="block w-8 h-1 bg-[#FF6600] rounded"></span>
                <span className="block w-8 h-1 bg-[#FF6600] rounded"></span>
                <span className="block w-8 h-1 bg-[#FF6600] rounded"></span>
              </button>
            </div>
            
            {/* Right - Language + Login */}
            <div className="flex items-center gap-2">
              <LanguageSelector variant="nav" />
              <button
                onClick={onLogin}
                className="px-3 py-1.5 bg-[#FF6600] text-white font-semibold rounded-full hover:bg-[#FF8533] transition-all text-xs"
                data-testid="nav-login-btn"
              >
                {t('login')}
              </button>
            </div>
          </div>
          
          {/* Desktop Layout - Logo left, Menu + Actions right */}
          <div className="hidden lg:flex items-center justify-between">
            {/* Left Side - Hamburger Menu + Logo */}
            <div className="flex items-center gap-4">
              {/* Hamburger Menu - Three Lines */}
              <button
                onClick={() => setMenuOpen(true)}
                className="text-white p-3 hover:bg-gray-800 rounded-lg transition-colors flex flex-col justify-center items-center gap-1.5"
                aria-label="Open menu"
                data-testid="hamburger-menu-btn-desktop"
              >
                <span className="block w-8 h-1 bg-[#FF6600] rounded"></span>
                <span className="block w-8 h-1 bg-[#FF6600] rounded"></span>
                <span className="block w-8 h-1 bg-[#FF6600] rounded"></span>
              </button>
              
              {/* Logo - Right next to hamburger */}
              <img src="/logo-v4.png" alt="Logo" className="h-20 w-20 object-contain" />
            </div>
            
            {/* Right Side Actions */}
            <div className="flex items-center gap-4">
              <LanguageSelector variant="nav" />
              <button
                onClick={onLogin}
                className="px-6 py-2 bg-[#FF6600] text-white font-semibold rounded-full hover:bg-[#FF8533] hover:shadow-lg hover:shadow-[#FF6600]/30 transition-all hover:-translate-y-0.5"
                data-testid="nav-login-btn"
              >
                {t('login')}
              </button>
              <button
                onClick={onGetStarted}
                className="px-6 py-2 bg-[#FF6600] text-white font-semibold rounded-full hover:bg-[#FF8533] hover:shadow-lg hover:shadow-[#FF6600]/30 transition-all hover:-translate-y-0.5"
                data-testid="nav-get-started-btn"
              >
                {t('get_started')}
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Side Menu Overlay */}
      {menuOpen && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-50 z-[60]"
          onClick={() => setMenuOpen(false)}
        >
          <div 
            className="w-80 h-full bg-white dark:bg-gray-800 shadow-2xl overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Menu Header */}
            <div className="bg-black p-4 flex items-center justify-between">
              <img src="/logo-v4.png" alt="Logo" className="h-12 w-12 object-contain" />
              <button
                onClick={() => setMenuOpen(false)}
                className="text-white p-2 hover:bg-gray-800 rounded-lg transition-colors"
                aria-label="Close menu"
              >
                <X size={24} />
              </button>
            </div>
            
            {/* NutriFitness AI Header */}
            <div className="p-4 border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
              <p className="font-bold text-gray-800 dark:text-gray-100 dark:text-gray-200 text-lg">{t('welcome')}</p>
            </div>
            
            {/* Menu Items */}
            <nav className="py-2">
              {menuItems.filter(item => !item.adminOnly).map((item) => (
                <div key={item.id} className="border-b border-gray-100 dark:border-gray-700">
                  {item.isExpandable ? (
                    <>
                      {/* Expandable Menu Header */}
                      <button
                        onClick={() => toggleSubmenu(item.id)}
                        className={`w-full flex items-center justify-between px-4 py-4 text-left transition-colors ${
                          expandedMenus[item.id] 
                            ? 'bg-gray-50 dark:bg-gray-900' 
                            : 'hover:bg-gray-50 dark:bg-gray-900'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <item.icon size={20} className="text-[#FF6600]" />
                          <span className="font-bold text-sm tracking-wide {text-gray-700 dark:text-gray-300">
                            {item.label}
                          </span>
                        </div>
                        <ChevronDown 
                          size={20} 
                          className={`text-gray-400 dark:text-gray-500 dark:text-gray-400 transition-transform duration-200 ${
                            expandedMenus[item.id] ? 'rotate-180' : ''
                          }`}
                        />
                      </button>
                      
                      {/* Sub-items */}
                      {expandedMenus[item.id] && (
                        <div className="bg-gray-50 dark:bg-gray-900 dark:bg-gray-700 py-1">
                          {item.subItems.map((subItem, idx) => (
                            <button
                              key={idx}
                              onClick={() => {
                                setMenuOpen(false);
                                onGetStarted();
                              }}
                              className="w-full flex items-center justify-between pl-12 pr-4 py-3 text-left transition-colors hover:bg-gray-100 dark:bg-gray-700"
                            >
                              <div>
                                <span className="text-sm font-semibold {text-gray-700 dark:text-gray-300">{subItem.label}</span>
                                {subItem.description && (
                                  <p className="text-xs text-gray-500 dark:text-gray-400 dark:text-gray-500 dark:text-gray-400">{subItem.description}</p>
                                )}
                              </div>
                              <ChevronRight size={16} className="text-gray-400 dark:text-gray-500 dark:text-gray-400" />
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    /* Non-expandable Menu Item */
                    <button
                      onClick={() => {
                        setMenuOpen(false);
                        onGetStarted();
                      }}
                      className="w-full flex items-center justify-between px-4 py-4 text-left transition-colors hover:bg-gray-50 dark:bg-gray-900"
                    >
                      <div className="flex items-center gap-3">
                        <item.icon size={20} className="text-[#FF6600]" />
                        <span className="font-bold text-sm tracking-wide {text-gray-700 dark:text-gray-300">{item.label}</span>
                      </div>
                      <ChevronRight size={20} className="text-gray-400 dark:text-gray-500 dark:text-gray-400" />
                    </button>
                  )}
                </div>
              ))}
            </nav>

            {/* Footer Links */}
            <div className="px-4 py-3 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
              <div className="flex justify-center gap-3 text-xs text-gray-500 dark:text-gray-400 dark:text-gray-500 dark:text-gray-400 flex-wrap">
                <button onClick={() => { setMenuOpen(false); onAbout && onAbout(); }} className="hover:text-[#FF6600] transition-colors">
                  {t('about_us')}
                </button>
                <span>|</span>
                <a href="#faq" onClick={(e) => { e.preventDefault(); setMenuOpen(false); document.querySelector('#faq-section')?.scrollIntoView({ behavior: 'smooth' }); }} className="hover:text-[#FF6600] transition-colors">
                  {t('faq')}
                </a>
                <span>|</span>
                <button onClick={() => { setMenuOpen(false); onPrivacy(); }} className="hover:text-[#FF6600] transition-colors">
                  {t('privacy')}
                </button>
                <span>|</span>
                <button onClick={() => { setMenuOpen(false); onTerms(); }} className="hover:text-[#FF6600] transition-colors">
                  {t('terms')}
                </button>
                <span>|</span>
                <a href="mailto:support@nutrifitness.us" className="hover:text-[#FF6600] transition-colors">
                  {t('contact')}
                </a>
              </div>
            </div>

            {/* CTA Button at Bottom */}
            <div className="p-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => {
                  setMenuOpen(false);
                  onGetStarted();
                }}
                className="w-full py-3 bg-[#FF6600] text-white font-bold rounded-lg hover:bg-[#E55C00] transition-colors"
              >
                {t('get_started_free')}
              </button>
              <button
                onClick={() => {
                  setMenuOpen(false);
                  onLogin();
                }}
                className="w-full py-3 mt-2 border-2 border-gray-300 text-gray-700 dark:text-gray-300 font-bold rounded-lg hover:border-[#FF6600] hover:text-[#FF6600] transition-colors"
              >
                {t('sign_in')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="pt-28 sm:pt-32 pb-12 sm:pb-20 px-4 sm:px-6 relative overflow-hidden">
        {/* Background Elements */}
        <div className="absolute top-20 right-0 w-64 sm:w-96 h-64 sm:h-96 bg-gradient-to-br from-orange-200 to-amber-100 rounded-full blur-3xl opacity-40 -z-10" />
        <div className="absolute bottom-0 left-0 w-48 sm:w-80 h-48 sm:h-80 bg-gradient-to-tr from-orange-100 to-yellow-100 rounded-full blur-3xl opacity-50 -z-10" />
        
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            {/* Left Content */}
            <div className="text-center lg:text-left">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold text-gray-900 dark:text-white leading-tight mb-4 mt-2">
                {t('hero_title_1')}
                <span className="block bg-gradient-to-r from-[#FF6600] via-[#FF7722] to-[#E55C00] bg-clip-text text-transparent">
                  {t('hero_title_2')}
                </span>
              </h1>

              <p className="text-lg sm:text-xl font-semibold text-gray-900 dark:text-white mb-3 italic">
                {t('hero_tagline')}
              </p>
              
              <p className="text-base sm:text-lg text-gray-600 dark:text-gray-300 mb-6 max-w-xl mx-auto lg:mx-0 leading-relaxed">
                Snap a photo, get instant calories. Personalized recipes for <strong className="text-purple-600">Organic</strong>, <strong className="text-emerald-600">Vegan</strong>, and <strong className="text-amber-600">Gluten-Free</strong> diets. Get guidance from our <strong className="text-blue-600">AI Nutritionist</strong> and <strong className="text-[#FF6600]">Exercise Coach</strong>.
              </p>
              
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mb-6 justify-center lg:justify-start">
                <button
                  onClick={onGetStarted}
                  className="group px-6 sm:px-8 py-3 bg-gradient-to-r from-[#FF6600] to-[#FF8533] text-white font-bold text-base rounded-full hover:shadow-xl hover:shadow-[#FF6600]/30 transition-all hover:-translate-y-1 flex items-center justify-center gap-2"
                  data-testid="hero-get-started-btn"
                >
                  {t('start_free_today')}
                  <ArrowRight size={20} className="group-hover:translate-x-1 transition-transform" />
                </button>
                <button
                  onClick={onLogin}
                  className="px-6 sm:px-8 py-3 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 font-bold text-base rounded-full border-2 border-gray-200 dark:border-gray-700 hover:border-[#FF6600] hover:text-[#FF6600] transition-all flex items-center justify-center gap-2"
                  data-testid="hero-login-btn"
                >
                  <Play size={20} />
                  {t('watch_demo')}
                </button>
              </div>
              
              {/* Trust Badges */}
              <div className="flex items-center gap-4 text-gray-500 dark:text-gray-400 dark:text-gray-500 dark:text-gray-400 justify-center lg:justify-start flex-wrap">
                <div className="flex items-center gap-2">
                  <Check size={18} className="text-green-500" />
                  <span className="text-sm">{t('free_scans')}</span>
                </div>
              </div>
            </div>
            
            {/* Right - App Preview */}
            <div className="relative lg:pl-10 hidden lg:block">
              <div className="relative">
                {/* Phone mockup */}
                <div className="bg-gradient-to-br from-gray-900 to-gray-800 rounded-[2.5rem] p-2 shadow-2xl shadow-gray-900/20 mx-auto max-w-xs">
                  <div className="bg-white dark:bg-gray-800 rounded-[2rem] overflow-hidden">
                    <div className="bg-gradient-to-br from-orange-400 to-[#FF8533] p-4 text-white">
                      <div className="text-xs opacity-80 mb-1">Today&apos;s Progress</div>
                      <div className="text-2xl font-bold">1,245 / 2,000</div>
                      <div className="text-xs opacity-80">calories</div>
                      <div className="mt-3 h-2 bg-white dark:bg-gray-800/30 rounded-full overflow-hidden">
                        <div className="h-full w-3/5 bg-white dark:bg-gray-800 rounded-full" />
                      </div>
                    </div>
                    <div className="p-3 space-y-2">
                      <div className="flex items-center gap-3 p-2 bg-gray-50 dark:bg-gray-900 dark:bg-gray-700 rounded-xl">
                        <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                          <Camera size={20} className="text-[#FF6600]" />
                        </div>
                        <div>
                          <div className="font-semibold text-gray-800 dark:text-gray-100 dark:text-gray-200 text-sm">Scan Meal</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 dark:text-gray-500 dark:text-gray-400">Instant analysis</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 p-2 bg-gray-50 dark:bg-gray-900 dark:bg-gray-700 rounded-xl">
                        <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                          <TrendingUp size={20} className="text-green-500" />
                        </div>
                        <div>
                          <div className="font-semibold text-gray-800 dark:text-gray-100 dark:text-gray-200 text-sm">Track Progress</div>
                          <div className="text-xs text-gray-500 dark:text-gray-400 dark:text-gray-500 dark:text-gray-400">See your gains</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-3 p-3 bg-gray-50 dark:bg-gray-900 dark:bg-gray-700 rounded-xl">
                        <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                          <MessageCircle size={24} className="text-blue-500" />
                        </div>
                        <div>
                          <div className="font-semibold text-gray-800 dark:text-gray-100 dark:text-gray-200">AI Nutritionist</div>
                          <div className="text-sm text-gray-500 dark:text-gray-400 dark:text-gray-500 dark:text-gray-400">24/7 guidance</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* Floating elements */}
                <div className="absolute -top-4 -right-4 bg-white dark:bg-gray-800 p-4 rounded-2xl shadow-xl animate-float">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <Check size={20} className="text-green-500" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold">Goal Reached!</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 dark:text-gray-500 dark:text-gray-400">Protein target</div>
                    </div>
                  </div>
                </div>
                
                <div className="absolute -bottom-4 -left-4 bg-white dark:bg-gray-800 p-4 rounded-2xl shadow-xl animate-float-delayed">
                  <div className="flex items-center gap-2">
                    <div className="text-2xl">🔥</div>
                    <div>
                      <div className="text-sm font-semibold">7 Day Streak</div>
                      <div className="text-xs text-gray-500 dark:text-gray-400 dark:text-gray-500 dark:text-gray-400">Keep it up!</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-8 sm:py-12 bg-black">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 sm:gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center text-white">
                <div className="text-2xl sm:text-4xl md:text-5xl font-bold mb-1 sm:mb-2 flex items-center justify-center gap-1 sm:gap-2 text-[#FF6600]">
                  {stat.value}
                  {stat.icon && <stat.icon size={20} fill="currentColor" className="sm:w-6 sm:h-6" />}
                </div>
                <div className="text-gray-300 font-medium text-xs sm:text-base">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-16 sm:py-24 px-4 sm:px-6 bg-gray-50 dark:bg-gray-900" data-animate>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">
              {t('features_title')}
              <span className="block text-[#FF6600]">{t('features_title_2')}</span>
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              {t('features_subtitle')}
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className={`group p-6 sm:p-8 bg-white dark:bg-gray-800 rounded-2xl sm:rounded-3xl border border-gray-100 dark:border-gray-700 hover:border-orange-200 transition-all duration-300 hover:shadow-xl hover:shadow-[#FF6600]/10 hover:-translate-y-2 cursor-pointer ${
                  activeFeature === index ? 'border-[#FF6600] shadow-lg shadow-[#FF6600]/10' : ''
                }`}
                onClick={() => setActiveFeature(index)}
              >
                <div className={`w-12 h-12 sm:w-16 sm:h-16 ${feature.bgColor} rounded-xl sm:rounded-2xl flex items-center justify-center mb-4 sm:mb-6 group-hover:scale-110 transition-transform`}>
                  <feature.icon size={28} className="sm:w-8 sm:h-8" style={{color: feature.color.includes('orange') ? '#f97316' : feature.color.includes('emerald') ? '#10b981' : feature.color.includes('blue') ? '#3b82f6' : feature.color.includes('purple') ? '#8b5cf6' : '#ef4444'}} />
                </div>
                <div className="inline-block px-2 sm:px-3 py-1 bg-gray-100 dark:bg-gray-700 rounded-full text-xs font-semibold text-gray-600 dark:text-gray-300 mb-3 sm:mb-4">
                  {feature.stat}
                </div>
                <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-3">{feature.title}</h3>
                <p className="text-gray-600 dark:text-gray-300 leading-relaxed text-sm sm:text-base">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-10 sm:mb-16">
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">
              {t('how_it_works')}
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-300">{t('how_it_works_subtitle')}</p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 relative">
            {/* Connection Line */}
            <div className="hidden md:block absolute top-20 left-1/4 right-1/4 h-0.5 bg-gradient-to-r from-orange-300 via-orange-500 to-orange-300" />
            
            {steps.map((step, index) => (
              <div key={index} className="relative text-center">
                <div className="w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-[#FF6600] to-[#FF8533] rounded-xl sm:rounded-2xl flex items-center justify-center mx-auto mb-4 sm:mb-6 text-white font-bold text-lg sm:text-xl shadow-lg shadow-[#FF6600]/30">
                  {step.step}
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-2 sm:mb-3">{step.title}</h3>
                <p className="text-gray-600 dark:text-gray-300">{step.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section id="faq-section" className="py-16 sm:py-24 px-4 sm:px-6 bg-gray-50 dark:bg-gray-900">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10 sm:mb-16">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-[#FF6600]/10 rounded-full mb-4">
              <HelpCircle size={32} className="text-[#FF6600]" />
            </div>
            <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg sm:text-xl text-gray-600 dark:text-gray-300">
              Everything you need to know about NutriFitness AI
            </p>
          </div>

          <div className="space-y-4">
            {faqItems.map((faq, index) => (
              <div 
                key={index}
                className="bg-white dark:bg-gray-800 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden transition-all hover:shadow-md"
              >
                <button
                  onClick={() => toggleFaq(index)}
                  className="w-full flex items-center justify-between p-5 sm:p-6 text-left"
                  data-testid={`faq-question-${index}`}
                >
                  <span className="font-semibold text-gray-900 dark:text-white pr-4 text-base sm:text-lg">
                    {faq.question}
                  </span>
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center transition-all ${
                    expandedFaq === index ? 'bg-[#FF6600] text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300'
                  }`}>
                    {expandedFaq === index ? (
                      <Minus size={18} />
                    ) : (
                      <Plus size={18} />
                    )}
                  </div>
                </button>
                
                {expandedFaq === index && (
                  <div className="px-5 sm:px-6 pb-5 sm:pb-6">
                    <div className="pt-2 border-t border-gray-100 dark:border-gray-700">
                      <p className="text-gray-600 dark:text-gray-300 leading-relaxed pt-4">
                        {faq.answer}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Still have questions? */}
          <div className="mt-12 text-center bg-gradient-to-r from-[#FF6600] to-[#FF8533] rounded-2xl p-8 sm:p-10">
            <h3 className="text-xl sm:text-2xl font-bold text-white mb-3">
              Still have questions?
            </h3>
            <p className="text-orange-100 mb-6">
              Our support team is here to help you 24/7
            </p>
            <a 
              href="mailto:support@nutrifitness.us"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white dark:bg-gray-800 text-[#FF6600] font-semibold rounded-full hover:shadow-lg transition-all hover:-translate-y-0.5"
            >
              <MessageCircle size={20} />
              Contact Support
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 sm:py-24 px-4 sm:px-6 bg-gradient-to-br from-[#FF6600] via-[#FF7722] to-[#E55C00] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-20 sm:w-40 h-20 sm:h-40 bg-white dark:bg-gray-800 rounded-full" />
          <div className="absolute bottom-10 right-10 w-32 sm:w-60 h-32 sm:h-60 bg-white dark:bg-gray-800 rounded-full" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-48 sm:w-80 h-48 sm:h-80 bg-white dark:bg-gray-800 rounded-full" />
        </div>
        
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-bold text-white mb-4 sm:mb-6">
            {t('cta_title')}
          </h2>
          <p className="text-lg sm:text-xl text-orange-100 mb-8 sm:mb-10 max-w-2xl mx-auto">
            {t('cta_subtitle')}
          </p>
          <button
            onClick={onGetStarted}
            className="group px-8 sm:px-10 py-4 sm:py-5 bg-white dark:bg-gray-800 text-[#FF6600] font-bold text-base sm:text-lg rounded-full hover:shadow-2xl transition-all hover:-translate-y-1 inline-flex items-center gap-2 sm:gap-3"
            data-testid="cta-get-started-btn"
          >
            {t('get_started_free')}
            <ArrowRight size={24} className="group-hover:translate-x-2 transition-transform" />
          </button>
          <p className="text-orange-100 mt-4 sm:mt-6 text-xs sm:text-sm">{t('free_scans')}</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-8 sm:py-12 px-4 sm:px-6 bg-gray-900 text-gray-400 dark:text-gray-500 dark:text-gray-400">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 sm:gap-6">
            <p className="text-xs sm:text-sm">© 2026 Nutrifitness AI. {t('all_rights_reserved')}.</p>
            <div className="flex gap-4 sm:gap-6 flex-wrap justify-center">
              <button 
                onClick={() => onLegalPage ? onLegalPage('terms-of-service') : onTerms?.()}
                className="hover:text-[#FF8533] transition-colors text-sm"
                data-testid="footer-terms"
              >
                Terms of Service
              </button>
              <button 
                onClick={() => onLegalPage ? onLegalPage('privacy-policy') : onPrivacy?.()}
                className="hover:text-[#FF8533] transition-colors text-sm"
                data-testid="footer-privacy"
              >
                Privacy Policy
              </button>
              <button 
                onClick={() => onLegalPage ? onLegalPage('health-disclaimer') : null}
                className="hover:text-[#FF8533] transition-colors text-sm"
                data-testid="footer-health-disclaimer"
              >
                Health Disclaimer
              </button>
              <button 
                onClick={() => onLegalPage ? onLegalPage('ai-disclaimer') : null}
                className="hover:text-[#FF8533] transition-colors text-sm"
                data-testid="footer-ai-disclaimer"
              >
                AI Disclaimer
              </button>
              <a href="mailto:support@nutrifitness.us" className="hover:text-[#FF8533] transition-colors text-sm">{t('contact')}</a>
            </div>
          </div>
        </div>
      </footer>

      {/* CSS Animations */}
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        @keyframes float-delayed {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
        .animate-float-delayed {
          animation: float-delayed 3s ease-in-out infinite;
          animation-delay: 1.5s;
        }
        .animate-fade-in {
          animation: fadeIn 0.6s ease-out;
        }
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(10px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      <VisitorTimeoutModal onSignup={onGetStarted} onLogin={onLogin} />
    </div>
  );
};

export default LandingPage;
